import React, { useState, useEffect } from 'react';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import RegistryCard from './components/RegistryCard';
import ActivityFeed from './components/ActivityFeed';
import BulkActions from './components/BulkActions';
import SystemHealth from './components/SystemHealth';

const RegistryConnectors = () => {
  const [registries, setRegistries] = useState([]);
  const [activities, setActivities] = useState([]);
  const [healthMetrics, setHealthMetrics] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  // Mock data for registries
  const mockRegistries = [
    {
      id: 'verra',
      name: 'Verra',
      type: 'Voluntary Carbon Standard',
      icon: 'Shield',
      status: 'connected',
      lastSync: '2 minutes ago',
      dataVolume: 2450000,
      syncFrequency: 'Every 15 minutes',
      syncProgress: 85,
      errorCount: 0
    },
    {
      id: 'gold-standard',
      name: 'Gold Standard',
      type: 'Voluntary Registry',
      icon: 'Award',
      status: 'connected',
      lastSync: '5 minutes ago',
      dataVolume: 1200000,
      syncFrequency: 'Every 30 minutes',
      syncProgress: 92,
      errorCount: 0
    },
    {
      id: 'acr',
      name: 'American Carbon Registry',
      type: 'Voluntary Registry',
      icon: 'MapPin',
      status: 'syncing',
      lastSync: '1 hour ago',
      dataVolume: 850000,
      syncFrequency: 'Every 1 hour',
      syncProgress: 45,
      errorCount: 0
    },
    {
      id: 'art',
      name: 'Architecture for REDD+ Transactions',
      type: 'REDD+ Registry',
      icon: 'TreePine',
      status: 'connected',
      lastSync: '15 minutes ago',
      dataVolume: 650000,
      syncFrequency: 'Every 2 hours',
      syncProgress: 100,
      errorCount: 0
    },
    {
      id: 'gcc',
      name: 'Global Carbon Council',
      type: 'Voluntary Registry',
      icon: 'Globe',
      status: 'error',
      lastSync: '3 hours ago',
      dataVolume: 320000,
      syncFrequency: 'Every 1 hour',
      syncProgress: 0,
      errorCount: 3
    },
    {
      id: 'cad-trust',
      name: 'CAD Trust',
      type: 'Digital Registry',
      icon: 'Database',
      status: 'connected',
      lastSync: '8 minutes ago',
      dataVolume: 180000,
      syncFrequency: 'Every 10 minutes',
      syncProgress: 78,
      errorCount: 0
    }
  ];

  // Mock data for activities
  const mockActivities = [
    {
      id: 1,
      type: 'sync',
      title: 'Scheduled sync completed',
      description: 'Successfully synchronized 15,420 credit records from Verra registry',
      registry: 'verra',
      registryName: 'Verra',
      timestamp: '2 minutes ago',
      duration: '45 seconds',
      recordsProcessed: '15,420',
      status: 'success'
    },
    {
      id: 2,
      type: 'connection',
      title: 'Registry connection established',
      description: 'Successfully connected to Gold Standard API endpoint',
      registry: 'gold-standard',
      registryName: 'Gold Standard',
      timestamp: '5 minutes ago',
      status: 'success'
    },
    {
      id: 3,
      type: 'data-ingestion',
      title: 'Data ingestion in progress',
      description: 'Processing methodology updates and credit issuances from ACR',
      registry: 'acr',
      registryName: 'ACR',
      timestamp: '12 minutes ago',
      duration: '2m 15s',
      recordsProcessed: '8,750',
      status: 'in-progress'
    },
    {
      id: 4,
      type: 'error',
      title: 'API rate limit exceeded',
      description: 'GCC API returned 429 status code. Implementing exponential backoff.',
      registry: 'gcc',
      registryName: 'GCC',
      timestamp: '1 hour ago',
      status: 'failed'
    },
    {
      id: 5,
      type: 'sync',
      title: 'Manual sync initiated',
      description: 'User-triggered synchronization for CAD Trust registry',
      registry: 'cad-trust',
      registryName: 'CAD Trust',
      timestamp: '2 hours ago',
      duration: '1m 32s',
      recordsProcessed: '3,240',
      status: 'success'
    }
  ];

  // Mock health metrics
  const mockHealthMetrics = {
    overall: 'good',
    uptime: 99.2,
    avgResponse: 245,
    syncSuccess: 94.8,
    errorRate: 2.1,
    registries: [
      { id: 'verra', name: 'Verra', icon: 'Shield', health: 'excellent', responseTime: 180, lastSync: '2 min ago' },
      { id: 'gold-standard', name: 'Gold Standard', icon: 'Award', health: 'good', responseTime: 220, lastSync: '5 min ago' },
      { id: 'acr', name: 'ACR', icon: 'MapPin', health: 'good', responseTime: 340, lastSync: '1 hour ago' },
      { id: 'art', name: 'ART', icon: 'TreePine', health: 'excellent', responseTime: 195, lastSync: '15 min ago' },
      { id: 'gcc', name: 'GCC', icon: 'Globe', health: 'poor', responseTime: 1200, lastSync: '3 hours ago' },
      { id: 'cad-trust', name: 'CAD Trust', icon: 'Database', health: 'good', responseTime: 280, lastSync: '8 min ago' }
    ],
    alerts: [
      {
        severity: 'high',
        title: 'GCC Registry Connection Issues',
        description: 'Multiple failed connection attempts detected. API may be experiencing downtime.',
        timestamp: '1 hour ago'
      },
      {
        severity: 'medium',
        title: 'High API Response Times',
        description: 'ACR registry showing elevated response times above normal thresholds.',
        timestamp: '3 hours ago'
      }
    ]
  };

  useEffect(() => {
    // Simulate loading data
    const loadData = async () => {
      setIsLoading(true);
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setRegistries(mockRegistries);
      setActivities(mockActivities);
      setHealthMetrics(mockHealthMetrics);
      setIsLoading(false);
    };

    loadData();
  }, []);

  const handleForceSync = async (registryId) => {
    setRegistries(prev => prev?.map(registry => 
      registry?.id === registryId 
        ? { ...registry, status: 'syncing', syncProgress: 0 }
        : registry
    ));

    // Simulate sync process
    setTimeout(() => {
      setRegistries(prev => prev?.map(registry => 
        registry?.id === registryId 
          ? { 
              ...registry, 
              status: 'connected', 
              syncProgress: 100,
              lastSync: 'Just now'
            }
          : registry
      ));
    }, 3000);
  };

  const handleViewLogs = (registryId) => {
    console.log('View logs for registry:', registryId);
    // In a real app, this would open a logs modal or navigate to logs page
  };

  const handleConfigure = (registryId) => {
    console.log('Configure registry:', registryId);
    // In a real app, this would open configuration modal
  };

  const handleBulkSync = async (registryIds, progressCallback) => {
    for (let i = 0; i < registryIds?.length; i++) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      progressCallback(i + 1, registryIds?.length);
    }
  };

  const handleBulkConfigure = (registryIds) => {
    console.log('Bulk configure registries:', registryIds);
  };

  const handleRefreshActivities = () => {
    // In a real app, this would fetch fresh activity data
    console.log('Refreshing activities...');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="flex items-center justify-center h-96">
          <div className="flex items-center space-x-3">
            <Icon name="RefreshCw" size={24} className="animate-spin text-primary" />
            <span className="text-lg text-muted-foreground">Loading registry connectors...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Registry Connectors</h1>
              <p className="text-muted-foreground mt-2">
                Manage and monitor digital MRV connections across carbon credit registries
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <div className="text-sm font-medium text-foreground">
                  {registries?.filter(r => r?.status === 'connected')?.length} of {registries?.length} Connected
                </div>
                <div className="text-xs text-muted-foreground">
                  Last updated: {new Date()?.toLocaleTimeString()}
                </div>
              </div>
              <Button
                variant="default"
                iconName="RefreshCw"
                iconPosition="left"
                onClick={() => window.location?.reload()}
              >
                Refresh All
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* System Health Overview */}
        <SystemHealth healthMetrics={healthMetrics} />

        {/* Registry Cards Grid */}
        <div>
          <h2 className="text-xl font-semibold text-foreground mb-6">Registry Connections</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {registries?.map((registry) => (
              <RegistryCard
                key={registry?.id}
                registry={registry}
                onForceSync={handleForceSync}
                onViewLogs={handleViewLogs}
                onConfigure={handleConfigure}
              />
            ))}
          </div>
        </div>

        {/* Bulk Actions */}
        <BulkActions
          registries={registries}
          onBulkSync={handleBulkSync}
          onBulkConfigure={handleBulkConfigure}
        />

        {/* Activity Feed */}
        <ActivityFeed
          activities={activities}
          onRefresh={handleRefreshActivities}
        />
      </div>
    </div>
  );
};

export default RegistryConnectors;